import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { getCurrentUser } from 'aws-amplify/auth';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
  ActiveFileFilterChip,
  buildActiveFileFilterChips,
  DateRangeFilter,
  EnrichedProgrammingFile,
  FileProgrammingRepository,
  FilesViewMode,
  PROGRAMMING_FILE_STATUS_OPTIONS,
  ProgrammingFilesFiltersQuery,
  ProgrammingFilesSortQuery,
  toProgrammingFileListItem
} from 'src/entities/file';
import { ChangeFileStateService } from 'src/features/file-programming/change-file-state/change-file-state.service';
import { OpenFileReviewDialogsService } from 'src/features/file-programming/open-file-review-dialogs/open-file-review-dialogs.service';

@Component({
  selector: 'app-file-programming',
  templateUrl: './file-programming.component.html',
  styleUrls: [
    './styles/file-programming.layout.css',
    './styles/file-programming.filters.css',
    './styles/file-programming.table.css',
    './styles/file-programming.skeleton.css',
    './styles/file-programming.responsive.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileProgrammingComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private filterFrameId: number | null = null;
  private searchDebounceId: number | null = null;
  private allClientOptions: string[] = [];
  private sourceFiles: EnrichedProgrammingFile[] = [];
  private pageCursors: Array<string | null> = [null];

  user: string | null = null;
  isLoading = false;
  isMergingData = false;
  isFiltering = false;
  loadingMessage = '';

  isFixedDepartureParentView = false;
  isChildFileView = false;
  viewMode: FilesViewMode = 'file';

  startDateFileRange: DateRangeFilter = { start: null, end: null };
  fileYear: string | null = new Date().getFullYear().toString();
  fileStatus: string | null = null;
  searchValue = '';
  searchValidationMessage = '';
  selectedNameClient: string | null = null;
  clientSearchValue = '';
  clientOptions: string[] = [];
  isLoadingClients = false;

  filteredFiles: EnrichedProgrammingFile[] = [];
  activeFilterChips: ActiveFileFilterChip[] = [];
  availableYears = ['2024', '2025', '2026', '2027'];
  fileStates: string[] = [...PROGRAMMING_FILE_STATUS_OPTIONS];
  pageIndex = 0;
  pageSize = 10;
  nextCursor: string | null = null;
  sort: ProgrammingFilesSortQuery = { field: 'updatedAt', direction: 'desc' };

  displayedColumns = [
    'file',
    'agency',
    'seller',
    'displayState',
    'operationDate',
    'createdAt',
    'updatedAt',
    'guide',
    'paxQuantity',
    'tcQuantity',
    'flights',
    'programmingReview',
    'bibleReview',
    'actions'
  ];

  readonly skeletonRows = Array(8).fill(null);
  readonly canChangeFileState = (file: EnrichedProgrammingFile): boolean =>
    this.changeFileState.hasManualOperationsStateChange(file);

  constructor(
    private readonly repository: FileProgrammingRepository,
    private readonly changeFileState: ChangeFileStateService,
    private readonly reviewDialogs: OpenFileReviewDialogsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  get pageTitle(): string {
    if (this.viewMode === 'parent') return 'Programación Files Padre';
    if (this.viewMode === 'child') return 'Programación Files Hijo';
    return 'Programación de Files';
  }

  get pageSubtitle(): string {
    if (this.viewMode === 'parent') {
      return 'Vista enfocada en files padre de salidas fijas para controlar la programación principal.';
    }
    if (this.viewMode === 'child') {
      return 'Vista enfocada solo en files hijo asociados a un file padre.';
    }
    return 'Vista general con files regulares, files padre y files hijo para programación operativa.';
  }

  get pageEyebrow(): string {
    if (this.viewMode === 'parent') return 'Files padre';
    if (this.viewMode === 'child') return 'Files hijo';
    return 'Programación';
  }

  get pageIcon(): string {
    if (this.viewMode === 'parent') return 'account_tree';
    if (this.viewMode === 'child') return 'subdirectory_arrow_right';
    return 'folder_open';
  }

  get pageHeroClass(): string {
    return `hero-${this.viewMode}`;
  }

  get hasActiveFilters(): boolean {
    return this.activeFilterChips.length > 0;
  }

  get paginatorLength(): number {
    return (
      this.pageIndex * this.pageSize +
      this.filteredFiles.length +
      (this.nextCursor ? this.pageSize : 0)
    );
  }

  ngOnInit(): void {
    this.loadCurrentUser();
    this.setupViewModeFromRoute();
    void this.loadClientFilterOptions();
    this.initLoad();
  }

  ngOnDestroy(): void {
    if (this.filterFrameId !== null) {
      cancelAnimationFrame(this.filterFrameId);
    }
    if (this.searchDebounceId !== null) {
      window.clearTimeout(this.searchDebounceId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.initLoad();
  }

  onTablePageChange(event: PageEvent): void {
    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
      void this.loadProgrammingFilesPage({
        resetPage: true,
        showLoading: true
      });
      return;
    }

    this.pageIndex = event.pageIndex;
    void this.loadProgrammingFilesPage({ resetPage: false, showLoading: true });
  }

  onTableSortChange(sort: Sort): void {
    const field = this.getSupportedSortField(sort.active);
    if (!field) return;

    this.sort = {
      field,
      direction: sort.direction === 'asc' ? 'asc' : 'desc'
    };
    void this.loadProgrammingFilesPage({
      resetPage: true,
      showFiltering: true
    });
  }

  onFileYearChange(value: string | null): void {
    this.fileYear = value ? value : null;
  }

  onFileStatusChange(value: string | null): void {
    this.fileStatus = value ? value : null;
  }

  onSearchValueChange(value: string): void {
    this.searchValue = value;
    this.updateSearchValidationMessage();
    this.refreshActiveFilterChips();
    this.cdRef.detectChanges();

    this.clearPendingSearchDebounce();

    this.searchDebounceId = window.setTimeout(() => {
      this.searchDebounceId = null;
      void this.loadProgrammingFilesPage({
        resetPage: true,
        showFiltering: true
      });
    }, 400);
  }

  onClientSearchChange(value: string): void {
    this.clientSearchValue = value;

    if (this.selectedNameClient && value !== this.selectedNameClient) {
      this.selectedNameClient = null;
      void this.loadProgrammingFilesPage({
        resetPage: true,
        showFiltering: true
      });
    }

    this.filterClientOptions(value);
  }

  onClientFilterChange(nameClient: string | null): void {
    this.selectedNameClient = nameClient;
    this.clientSearchValue = nameClient ?? '';

    if (!nameClient) {
      this.filterClientOptions('');
    }

    void this.loadProgrammingFilesPage({
      resetPage: true,
      showFiltering: true
    });
  }

  queueFilterSelect(): void {
    if (this.filterFrameId !== null) {
      cancelAnimationFrame(this.filterFrameId);
    }
    this.isFiltering = true;
    this.refreshActiveFilterChips();
    this.cdRef.detectChanges();
    this.filterFrameId = requestAnimationFrame(() => {
      this.filterFrameId = null;
      this.loadProgrammingFilesPage({ resetPage: true, showFiltering: true });
    });
  }

  removeFilter(id: string): void {
    switch (id) {
      case 'year':
        this.fileYear = null;
        break;
      case 'status':
        this.fileStatus = null;
        break;
      case 'search':
        this.searchValue = '';
        this.searchValidationMessage = '';
        this.clearPendingSearchDebounce();
        break;
      case 'startDateFile':
        this.startDateFileRange = { start: null, end: null };
        break;
      case 'nameClient':
        this.selectedNameClient = null;
        this.clientSearchValue = '';
        this.filterClientOptions('');
        break;
    }
    this.loadProgrammingFilesPage({ resetPage: true, showFiltering: true });
  }

  clearAllFilters(): void {
    this.fileYear = null;
    this.fileStatus = null;
    this.searchValue = '';
    this.searchValidationMessage = '';
    this.clearPendingSearchDebounce();
    this.selectedNameClient = null;
    this.clientSearchValue = '';
    this.filterClientOptions('');
    this.startDateFileRange = { start: null, end: null };
    this.loadProgrammingFilesPage({ resetPage: true, showFiltering: true });
  }

  cleanDateRange(rangeId: 'startDateFileRange'): void {
    void rangeId;
    this.startDateFileRange = { start: null, end: null };
    this.queueFilterSelect();
  }

  onGo(file: EnrichedProgrammingFile): void {
    const id = file?.id;
    if (!id) return;

    if (
      file.programmingViewType === 'PARENT' ||
      this.isFixedDepartureParentView
    ) {
      this.router.navigate(['/salidas-fijas', id]);
    } else if (file.programmingViewType === 'CHILD' || this.isChildFileView) {
      this.router.navigate(['/file-hijo', id]);
    } else {
      this.router.navigate(['/file', id]);
    }
  }

  openDialogPasajeros(file: EnrichedProgrammingFile): void {
    this.reviewDialogs
      .openPassengers(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  openDialogVuelos(fileId: string | null | undefined): void {
    this.reviewDialogs
      .openFlights(fileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  openDialogEstadoFIle(): void {
    this.reviewDialogs
      .openFileStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  openDialogEstadoFile(file: EnrichedProgrammingFile): void {
    this.changeFileState
      .requestStateChange(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (!result) return;
        this.applyStateChangeResult(file, result);
      });
  }

  openRevisionACDialog(file: EnrichedProgrammingFile): void {
    this.reviewDialogs
      .openCsReview(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pendingRevisionAc: any) => {
        if (
          typeof pendingRevisionAc !== 'number' &&
          pendingRevisionAc != null
        ) {
          return;
        }

        this.patchFileInLists(file.id, {
          pendingCsReviews: pendingRevisionAc
        });
      });
  }

  openPreOperationalIncidentsDialog(file: EnrichedProgrammingFile): void {
    this.reviewDialogs
      .openPreOperationalIncidents(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pendingPreOpIncidents: any) => {
        if (
          typeof pendingPreOpIncidents !== 'number' &&
          pendingPreOpIncidents != null
        ) {
          return;
        }

        this.patchFileInLists(file.id, {
          pendingPreOpIncidents
        });
      });
  }

  private async loadCurrentUser(): Promise<void> {
    try {
      const user = await getCurrentUser();
      this.user = user.signInDetails?.loginId || user.username;
    } catch (error) {
      console.error('Error al obtener usuario actual:', error);
      this.user = 'usuario-desconocido';
    }
  }

  private setupViewModeFromRoute(): void {
    const currentUrl = this.router.url.split('?')[0];
    this.viewMode = 'file';
    this.isChildFileView = false;
    this.isFixedDepartureParentView = false;

    if (currentUrl.includes('/file-hijo/programacion')) {
      this.isChildFileView = true;
      this.viewMode = 'child';
      return;
    }

    if (currentUrl.includes('/salidas-fijas/programacion')) {
      this.isFixedDepartureParentView = true;
      this.viewMode = 'parent';
      this.displayedColumns = this.displayedColumns.filter(
        (column) => !['agency', 'seller'].includes(column)
      );
    }
  }

  private async initLoad(): Promise<void> {
    this.destroy$.next();
    this.isLoading = true;
    this.isMergingData = false;
    this.loadingMessage = 'Iniciando carga...';
    this.filteredFiles = [];
    this.sourceFiles = [];
    this.pageIndex = 0;
    this.pageCursors = [null];
    this.nextCursor = null;
    this.cdRef.detectChanges();

    await this.loadProgrammingFilesPage({ resetPage: true, showLoading: true });
  }

  private async loadProgrammingFilesPage({
    resetPage,
    showFiltering = false,
    showLoading = false
  }: {
    resetPage: boolean;
    showFiltering?: boolean;
    showLoading?: boolean;
  }): Promise<void> {
    if (resetPage) {
      this.pageIndex = 0;
      this.pageCursors = [null];
      this.nextCursor = null;
    }

    this.refreshActiveFilterChips();
    this.isFiltering = showFiltering;
    this.isLoading = showLoading;
    this.loadingMessage = 'Preparando página de programación...';
    this.cdRef.detectChanges();

    try {
      const cursor = this.pageCursors[this.pageIndex] ?? null;
      const response: any = await firstValueFrom(
        this.repository.getProgrammingFilesPage({
          view: this.viewMode,
          limit: this.pageSize,
          cursor,
          filters: this.buildProgrammingFilesFilters(),
          sort: this.sort
        })
      );

      const page = response?.getProgrammingFilesPage;
      const items = page?.items ?? [];
      this.nextCursor = page?.nextCursor ?? null;

      if (this.nextCursor) {
        this.pageCursors[this.pageIndex + 1] = this.nextCursor;
      } else {
        this.pageCursors = this.pageCursors.slice(0, this.pageIndex + 1);
      }

      this.sourceFiles = items.map(toProgrammingFileListItem);

      this.filteredFiles = [...this.sourceFiles];
      this.loadingMessage = `${this.sourceFiles.length} registros cargados`;
    } catch (error) {
      console.error('Error loading programming files:', error);
      this.sourceFiles = [];
      this.filteredFiles = [];
    } finally {
      this.isLoading = false;
      this.isFiltering = false;
      this.cdRef.detectChanges();
    }
  }

  private buildProgrammingFilesFilters(): ProgrammingFilesFiltersQuery {
    const filters: ProgrammingFilesFiltersQuery = {};
    const rawSearch = this.searchValue.trim();
    const canSendSearch = this.canSendSearch(rawSearch);

    if (this.fileYear) filters.year = this.fileYear;
    if (this.fileStatus) filters.status = this.fileStatus;
    if (canSendSearch) filters.search = rawSearch;
    if (this.selectedNameClient) {
      filters.nameClient = this.selectedNameClient;
    }
    const formatFilterYmd = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (this.startDateFileRange.start) {
      filters.startDateFileFrom = formatFilterYmd(
        this.startDateFileRange.start
      );
    }
    if (this.startDateFileRange.end) {
      filters.startDateFileTo = formatFilterYmd(this.startDateFileRange.end);
    }
    return filters;
  }

  private refreshActiveFilterChips(): void {
    const rawSearch = this.searchValue.trim();

    this.activeFilterChips = buildActiveFileFilterChips({
      fileStatus: this.fileStatus,
      fileYear: this.fileYear,
      nameClient: this.selectedNameClient,
      searchValue: this.canSendSearch(rawSearch) ? rawSearch : '',
      startDateFileRange: this.startDateFileRange
    });
  }

  private async loadClientFilterOptions(): Promise<void> {
    this.isLoadingClients = true;
    this.cdRef.detectChanges();

    try {
      const response = await firstValueFrom(
        this.repository.getSalesClientFilterOptions({
          limit: 200
        })
      );

      this.allClientOptions = (response?.getSalesClientFilterOptions ?? [])
        .map((option) => option?.nameClient)
        .filter((nameClient): nameClient is string => !!nameClient);
      this.filterClientOptions(this.clientSearchValue);
    } catch (error) {
      console.error('Error loading sales client filter options:', error);
      this.allClientOptions = [];
      this.clientOptions = [];
    } finally {
      this.isLoadingClients = false;
      this.cdRef.detectChanges();
    }
  }

  private applyFilterSelect(): void {
    void this.loadProgrammingFilesPage({
      resetPage: true,
      showFiltering: true
    });
  }

  private patchFileInLists(
    fileId: string | null | undefined,
    patch: Partial<EnrichedProgrammingFile>
  ): void {
    if (!fileId) {
      return;
    }

    const applyPatch = (files: EnrichedProgrammingFile[]) =>
      files.map((item) => (item.id === fileId ? { ...item, ...patch } : item));

    this.sourceFiles = applyPatch(this.sourceFiles);
    this.filteredFiles = applyPatch(this.filteredFiles);
    this.cdRef.detectChanges();
  }

  private applyStateChangeResult(
    file: EnrichedProgrammingFile,
    result: any
  ): void {
    const targetState = result?.targetState;

    if (result?.updated !== true || !targetState) {
      this.applyFilterSelect();
      return;
    }

    const updatedFile = toProgrammingFileListItem({
      ...file,
      displayState: targetState,
      rowState: 'NORMAL',
      updatedAt: new Date().toISOString()
    });

    this.sourceFiles = this.replaceOrRemoveChangedStateFile(
      this.sourceFiles,
      updatedFile
    );
    this.filteredFiles = this.replaceOrRemoveChangedStateFile(
      this.filteredFiles,
      updatedFile
    );
    this.loadingMessage = 'Estado actualizado en pantalla';
    this.cdRef.detectChanges();
  }

  private replaceOrRemoveChangedStateFile(
    files: EnrichedProgrammingFile[],
    updatedFile: EnrichedProgrammingFile
  ): EnrichedProgrammingFile[] {
    if (!updatedFile.id) return files;

    if (this.fileStatus && this.fileStatus !== updatedFile.displayState) {
      return files.filter((file) => file.id !== updatedFile.id);
    }

    return files.map((file) =>
      file.id === updatedFile.id ? updatedFile : file
    );
  }

  private getSupportedSortField(
    active: string
  ): ProgrammingFilesSortQuery['field'] | null {
    if (active === 'updatedAt' || active === 'startDateFile') {
      return active;
    }
    return null;
  }

  private canSendSearch(rawSearch: string): boolean {
    if (!rawSearch) return false;

    const isNumericSearch = /^\d+$/.test(rawSearch);
    return !isNumericSearch || rawSearch.length >= 4;
  }

  private updateSearchValidationMessage(): void {
    const rawSearch = this.searchValue.trim();
    const isShortNumericSearch =
      /^\d+$/.test(rawSearch) && rawSearch.length < 4;

    this.searchValidationMessage = isShortNumericSearch
      ? 'Ingresa al menos 4 dígitos para buscar por número de file'
      : '';
  }

  private clearPendingSearchDebounce(): void {
    if (this.searchDebounceId === null) return;

    window.clearTimeout(this.searchDebounceId);
    this.searchDebounceId = null;
  }

  private filterClientOptions(search: string): void {
    const rawSearch = search.trim();

    if (!rawSearch) {
      this.clientOptions = [...this.allClientOptions];
      return;
    }

    const normalizedSearch = this.normalizeClientSearch(rawSearch);
    this.clientOptions = this.allClientOptions.filter((nameClient) =>
      this.normalizeClientSearch(nameClient).includes(normalizedSearch)
    );
  }

  private normalizeClientSearch(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }
}
